'use client';

import React, {useEffect} from 'react';
import SkillzStyleProvider from './SkillzStyleProvider';
import ImportGameTheme from '@/app/(app)/games/[gid]/_src/ImportGameTheme';
import GeoLocation from '@/app/(app)/games/[gid]/_src/GeoLocation';
import {
  NotificationNavigationController,
  BadgeController,
  AssetHelper,
  SkillzUI,
} from '@sdk-mobile';
import {getStoredSettings} from '@/services/internal/settings/client';
import useSyncSettings from '@/app/(app)/games/[gid]/_src/useSyncSettings';
import AuthHandler from '@/modules/AuthHandler';
import GlobalSubscriptions from '@/modules/GlobalSubscriptions';
import SDKProvider from '@/app/(app)/games/[gid]/_src/SkillzSDK/SDKProvider';
import MaintenancePopupWeb from '@/sdk-mobile-web/MaintenancePopupWrapper/MaintenancePopupWrapper';
import partnerizeShellTheme from '@/app/(app)/partner/[slug]/_src/partnerizeShellTheme';
import type {ResolvedTheme} from '@/services/partner/types';

const {AssetsProvider} = SkillzUI;

interface SkillzSettingsProviderProps {
  gid: string;
  children: React.ReactNode;
  isAuthenticated?: boolean;
  /**
   * Partner "Theme game screens" override (§10.2): when a partner with
   * `useOwnTheme` deep-links into a game, its resolved theme is passed here and the
   * full lobby SURFACE overlay (page/card/text + brand) is applied on top of the
   * game's own theme. Absent → the game renders in its OWN theme (the default, and
   * what Lycos/standard partners always get). Set by the game layout off the cookie.
   */
  partnerLobbyTheme?: ResolvedTheme | null;
}

export default function SkillzSettingsProvider({
  children,
  gid,
  isAuthenticated,
  partnerLobbyTheme,
}: SkillzSettingsProviderProps) {
  const rawSettings = getStoredSettings();
  // Overlay the partner lobby theme only when the partner opted into theming game
  // screens; otherwise the game keeps its own theme untouched.
  const settings =
    partnerLobbyTheme && rawSettings
      ? partnerizeShellTheme(rawSettings, partnerLobbyTheme, {surfaces: true})
      : rawSettings;
  const synced = useSyncSettings(gid);

  useEffect(() => {
    if (isAuthenticated && settings && synced) {
      BadgeController.checkAccountMenuBadges(true, true);
    }
  }, [settings, synced, isAuthenticated]);

  if (!settings || !synced) {
    return null;
  }

  return (
    <SkillzStyleProvider
      themeJSON={settings.theme.gameTheme}
      themeImagesUrl={settings.theme.themeImagesUrl}
      themeImages={settings.theme.themeImages}
      sourceBaseUrl={settings.sourceBaseUrl}
      customThemeUrl={settings.theme.customThemeUrl}
      baseThemeUrl={settings.theme.baseThemeUrl}
      localImages={settings.localImages}
      gameIcon={settings.gameInfo.iconUrl || ''}
      gameTitle={settings.gameInfo.title || ''}
    >
      <GlobalSubscriptions gid={gid} />
      <AuthHandler />
      <ImportGameTheme themeColors={settings.baseTheme} />
      <GeoLocation geoSrc={settings.geoSrc} />
      <AssetsProvider getBaseURL={() => AssetHelper.pathForSkillzUIPackage(settings.sourceBaseUrl)}>
        <SDKProvider>{children}</SDKProvider>
      </AssetsProvider>
      <NotificationNavigationController />
      <MaintenancePopupWeb />
    </SkillzStyleProvider>
  );
}
